import { useTranslation } from 'react-i18next'
import { Controller, useForm } from 'react-hook-form'
import { useQueryClient } from '@tanstack/react-query'
import { ErrorMessage } from '@hookform/error-message'
import { dashboardClient } from '@/api'
import {
  CheckWebhookItemRequest,
  CheckWebhookResponse,
  HTTPValidationError,
  HttpResponse,
  WebhookEventType,
  WebhookRedacted,
} from '@/api/dashboard'
import { FieldGroup } from '@/ui'
import { refetchWebhookLogQuery } from '../api'
import { refetchWebhookStatQuery, refetchWebhookTotalStatQuery } from '@/layouts/webhooks/api/webhooksStatQuery'
import {
  Button,
  CollapseView,
  FormErrorMessage,
  Input,
  Modal,
  ModalContent,
  ModalFooter,
  ModalProps,
  ModalTitle,
  Select,
  Textarea,
} from '@dashboard/ui'
import SelectSkuPanel from '@/layouts/components/SelectSkuPanel'
import { useGenerateCompanyPath } from '@/libs'
import { useEffect, useState } from 'react'
import { filterWebhookEvents } from '../libs/filterWebhookEvents'

interface WebhookTestModalProps extends ModalProps {
  webhook: WebhookRedacted
}

interface WebhookTestData extends CheckWebhookItemRequest {
  store_id?: string
  item_id?: string
}

export const WebhookTestModal = ({ webhook, ...rest }: WebhookTestModalProps) => {
  const ql = useQueryClient()
  const { t } = useTranslation()
  const { companyId, gameId } = useGenerateCompanyPath()
  const [errorResponse, setErrorResponse] = useState<CheckWebhookResponse | null>(null)

  const {
    formState: { errors, isSubmitting },
    handleSubmit,
    control,
    watch,
    setValue,
    register,
    clearErrors,
  } = useForm<WebhookTestData>({
    defaultValues: {},
  })

  const store_id = watch('store_id')
  const item_id = watch('item_id')
  const type = watch('event_type')

  useEffect(() => {
    setErrorResponse(null)
    clearErrors()
  }, [type])

  const onSubmit = handleSubmit(async data => {
    setErrorResponse(null)
    let promise: Promise<HttpResponse<CheckWebhookResponse, HTTPValidationError>> | null
    switch (type) {
      case WebhookEventType.ItemAdd:
      case WebhookEventType.ItemRemove:
        promise = dashboardClient.v1.checkWebhookItem(companyId, gameId, webhook.id, data)
        break
      case WebhookEventType.PlayerVerify:
        promise = dashboardClient.v1.checkWebhookPlayerVerify(companyId, gameId, webhook.id, {
          player_id: data.player_id!,
        })
        break
      default:
        promise = dashboardClient.v1.checkWebhook(companyId, gameId, webhook.id, {
          event_type: data.event_type,
        })
        break
    }

    const response = await promise
    if (response.data.is_error) {
      setErrorResponse(response.data)
      return
    }

    await new Promise(r => setTimeout(r, 4000))

    await Promise.all([
      refetchWebhookLogQuery(ql, companyId, gameId, webhook.id),
      refetchWebhookStatQuery(ql, companyId, gameId, webhook.id),
      refetchWebhookTotalStatQuery(ql, companyId, gameId, webhook.id),
    ])

    rest.onClose?.()
  })

  const renderErrorMessage = () => {
    if (!errorResponse) {
      return null
    }

    if (errorResponse.response_text) {
      return (
        <CollapseView label={<span className="text-text-error-secondary">{errorResponse.detail}</span>}>
          <Textarea value={errorResponse.response_text} className="mb-2" />
          {type == WebhookEventType.PlayerVerify && (
            <a href="https://docs.aghanim.com/webhooks/verify-player" target="_blank">
              <span className="cursor-pointer text-caption-md text-text-secondary">
                {t('webhook.test-modal.learn-more')}
              </span>
            </a>
          )}
        </CollapseView>
      )
    }

    return <FormErrorMessage>{errorResponse.detail}</FormErrorMessage>
  }

  const renderExtFormFields = () => {
    if (type == WebhookEventType.ItemAdd || type == WebhookEventType.ItemRemove) {
      return (
        <>
          <Controller
            control={control}
            name="item_id"
            rules={{ required: t('validation.required') }}
            render={({ field }) => (
              <SelectSkuPanel
                onChange={(
                  item_id: string | null | undefined,
                  store_id: string | null | undefined,
                  store_item_id: string | null | undefined,
                ) => {
                  setValue('store_id', store_id as string)
                  setValue('store_item_id', store_item_id as string)
                  field.onChange(item_id)
                }}
                storeId={store_id}
                storeItemId={field.value}
                itemId={item_id}
              />
            )}
          />
          <ErrorMessage
            name="item_id"
            errors={errors}
            render={({ message }) => <FormErrorMessage>{message}</FormErrorMessage>}
          />

          <FieldGroup label={t('players.ext_id')}>
            <Input {...register('player_id')} />
            <ErrorMessage
              name="player_id"
              errors={errors}
              render={({ message }) => <FormErrorMessage>{message}</FormErrorMessage>}
            />
          </FieldGroup>
        </>
      )
    }

    if (type == WebhookEventType.PlayerVerify) {
      return (
        <FieldGroup label={t('players.ext_id')}>
          <Input {...register('player_id')} />
          <ErrorMessage
            name="player_id"
            errors={errors}
            render={({ message }) => <FormErrorMessage>{message}</FormErrorMessage>}
          />
        </FieldGroup>
      )
    }
  }

  return (
    <Modal {...rest}>
      <form onSubmit={onSubmit}>
        <ModalContent>
          <ModalTitle>{t('webhook.test-modal.title')}</ModalTitle>

          <FieldGroup label={t('webhook.test-modal.label')}>
            <Controller
              control={control}
              name="event_type"
              rules={{ required: t('validation.required') }}
              render={({ field }) => (
                <Select
                  {...field}
                  items={[
                    ...filterWebhookEvents(Object.values(WebhookEventType))
                      .filter(event => webhook.events.includes(event))
                      .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
                      .map(event => ({
                        children: event,
                        value: event,
                      })),
                  ]}
                />
              )}
            />
            <ErrorMessage
              name="event_type"
              errors={errors}
              render={({ message }) => <FormErrorMessage>{message}</FormErrorMessage>}
            />
          </FieldGroup>

          {renderExtFormFields()}

          {renderErrorMessage()}
        </ModalContent>

        <ModalFooter>
          <Button type="button" color="secondary" variant="outline" onClick={rest.onClose}>
            {t('webhook.test-modal.cancel-button')}
          </Button>
          <Button color="primary" type="submit" loading={isSubmitting}>
            {t('webhook.test-modal.send-button')}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  )
}
